import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, resolveDirective as _resolveDirective, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "col"
}
const _hoisted_2 = { class: "row flex-wrap mleft-42px" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "rowcenter" }
const _hoisted_5 = ["src"]

import EsContentCalendar from '@/components/calendar/esContentCalendar.vue';
import { CalendarView } from '@/models/calendar.model';
import { ref, onMounted, watch, computed } from 'vue';
import EsTemplateEvent from '@/components/calendar/esTemplateEvent.vue';
import { ItemTag, ItemSelect, ApiError } from '@/models/share.model';
import { useCurrentEntryStore } from '@/stores/currentEntry';
import EsCalendarHeader from '@/components/calendar/esCalendarHeader.vue';
import { useI18n } from 'vue-i18n';
import PersonService from "@/services/PersonService";
import { AppointmentResponse } from '@/apiGenerate/medserver';
import { useAppUserStore } from '@/stores/appUser';
import { CheckAdminPlanningPermissionResponse, SettingsApi } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { toastError } from '@/utils/toast-utils';
import { useToast } from 'primevue';
import SharedService from "@/services/SharedService";


export default /*@__PURE__*/_defineComponent({
  __name: 'esWeeklyCalendar',
  props: {
    selectedDate: {},
    getEvents: { type: Function },
    doctors: {},
    refresh: { type: Boolean },
    patient: {},
    getBusinessHours: { type: Boolean },
    selectedDoctorAppoitmentTypes: {},
    changeDate: {},
    changeAgendaType: {},
    changeAppointmentType: {},
    changePatient: {},
    changeDoctor: {},
    changeDoctorList: {}
  },
  emits: ['openAddForm', 'refreshed', 'switchOpened', 'selectAppointmentType', 'refreshCalendar', 'openUpdateEvent', 'saveLastDoctorOpened', 'patientTagClick', 'openSettings'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const {t} = useI18n();
const toast = useToast();

const settingsApi = new SettingsApi(medServerApiConfig);

const appointmentTypeList = ref<ItemTag[]>([]);
const backupPatient = ref<ItemSelect>(null);
const doctorOpened = ref<ItemTag>(null);
const unselectAppointmentType = ref<boolean>(false);
const doctorAppointmentTypeSelected = ref<ItemTag>(undefined);
const displaySettings = ref<boolean>(false);

const doctorOpenedId = computed(() => doctorOpened.value?.id);

const selectedAppointmentType = ref<ItemTag>(props.selectedDoctorAppoitmentTypes && doctorOpenedId.value ? props.selectedDoctorAppoitmentTypes[doctorOpenedId.value] : null);

const currentEntryStore = useCurrentEntryStore();
const appUserStore = useAppUserStore();

const emits = __emit;

const currPersonWithRoleUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership || !appUserStore.currentHealthGroupMembership.pwr) {
    return null;
  }
  return appUserStore.currentHealthGroupMembership.pwr.personWithRoleId;
});

const currInstituteUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  const currentIwr = appUserStore.currentHealthGroupMembership.instituteWithRole;
  return currentIwr.instituteWithRoleId;
});

const checkSettingsPermission = async () => {
  if (doctorOpenedId.value) {
    if (doctorOpenedId.value === currPersonWithRoleUuid.value) {
      displaySettings.value = true;
    } else {
      await settingsApi.checkHealthProForAdminPlanningPermissionForHealthPro({instituteId: currInstituteUuid.value, personId: doctorOpenedId.value})
      .then((result: CheckAdminPlanningPermissionResponse) => {
        displaySettings.value = result.allowed;
      }).catch((error: ApiError) => {
            toastError(toast, {
                summary: t('common.toast.error'),
                detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
            });
        });
    }
  }
}

const changeDoctorOpened = (doctor: ItemTag) => {
  emits('switchOpened', doctor);
}

const selectAppointmentType = (data: {appointmentType: ItemTag, doctor: ItemTag}) => {
  if (data) {
    doctorAppointmentTypeSelected.value = data.doctor;
    unselectAppointmentType.value = true;

    if (data.appointmentType) {
      const index = appointmentTypeList.value.findIndex((appointmentType)=> appointmentType.id === data.appointmentType.id);
      if (index >= 0) {
        appointmentTypeList.value.forEach((appointmentType) => appointmentType.selected = false);
        appointmentTypeList.value[index].selected = true;
      }
      emits('selectAppointmentType', {appointmentType: data.appointmentType, doctorId: data?.doctor ? data.doctor.id : null});
    }
  }
}

const patientTagClick = async () => {
  await currentEntryStore.setCurrentPatientCalendar(null);
  emits('patientTagClick', null);
}

const setUnselectVariable = (newValue: boolean) => {
  unselectAppointmentType.value = newValue;
  doctorAppointmentTypeSelected.value = undefined;
}

const getTooltip = (appointment: AppointmentResponse) => {
  if (appointment.extendedProps.failedToFetchPortalPatientData) {
      return { 
          value: `<span class='font-tooltip'>${t('common.appointment.patientWarningMsg')}</span><br>
                  <span class='font-tooltip'>${appointment.title ?? ''}</span><br>
                  <span class='font-tooltip'>${t(`common.appointment.status.${appointment.extendedProps?.status ?? ''}`)}</span><br>
                  <span class='font-tooltip'>${appointment.extendedProps?.comment ?? ''}</span>`,
          escape: false,
          classes: 'custom-tooltip-class'
      }
  }

  return { 
      value: `<strong>${PersonService.formatHonorificAndName(appointment.extendedProps?.gender, appointment.extendedProps?.lastname, appointment.extendedProps?.firstname) ?? ''}</strong><br>
              <span class='font-tooltip'>${appointment.title ?? ''}</span><br>
              <span class='font-tooltip'>${t(`common.appointment.status.${appointment.extendedProps?.status ?? ''}`)}</span><br>
              <span class='font-tooltip'>${appointment.extendedProps?.comment ?? ''}</span>`,
      escape: false,
      classes: 'custom-tooltip-class'
  };
}

const refreshDoctorOpened = () => {
  const saveDoctor = !doctorOpened.value;
    doctorOpened.value = props.doctors.filter((doctor) => doctor.opened)[0];

    if (saveDoctor) {
      emits('saveLastDoctorOpened', doctorOpened.value);
    }
}


watch(
  () => props.refresh,
  () => {
    if (props.refresh && props.selectedDoctorAppoitmentTypes) {
      selectedAppointmentType.value = props.selectedDoctorAppoitmentTypes && doctorOpenedId.value ? props.selectedDoctorAppoitmentTypes[doctorOpenedId.value] : null;
      refreshDoctorOpened();
    }
  }
)

watch(
  () => doctorOpened.value,
  () => {
    selectedAppointmentType.value = props.selectedDoctorAppoitmentTypes && doctorOpenedId.value ? props.selectedDoctorAppoitmentTypes[doctorOpenedId.value] : null;
  }
)

watch(
  () => props.doctors,
  () => {
    refreshDoctorOpened();
  }
)

watch(
  () => props.patient,
  () => {
    if (props.patient) {
      if (!backupPatient.value || backupPatient.value.id !== props.patient.id) {
        backupPatient.value = props.patient;
      }
    }
  }
)

watch(
  () => props.doctors,
  (doctorList) => {
    if (doctorList.length > 0) {
      checkSettingsPermission();
    }
  }
)

onMounted(() => {
    if (props.doctors.length > 0) {
      doctorOpened.value = props.doctors.filter((doctor) => doctor.opened)[0];
      emits('saveLastDoctorOpened', doctorOpened.value);
      checkSettingsPermission();
    }
})

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (doctorOpenedId.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(EsCalendarHeader, {
          itemTag: doctorOpened.value,
          displayInitial: true,
          patient: _ctx.patient,
          displayCreateAppointment: true,
          selectedAppointmentType: selectedAppointmentType.value,
          unselectAppointmentType: unselectAppointmentType.value && doctorOpenedId.value!== doctorAppointmentTypeSelected.value.id,
          onSelectAppointmentType: _cache[0] || (_cache[0] = ($event: any) => (selectAppointmentType($event))),
          onPatientTagClick: _cache[1] || (_cache[1] = ($event: any) => (patientTagClick())),
          onSetUnselectedAppointmentType: _cache[2] || (_cache[2] = ($event: any) => (setUnselectVariable($event)))
        }, null, 8, ["itemTag", "patient", "selectedAppointmentType", "unselectAppointmentType"]),
        _createVNode(EsContentCalendar, {
          displayDayHeaders: true,
          getEvents: _ctx.getEvents,
          calendarView: _unref(CalendarView).WEEK,
          selectedDate: _ctx.selectedDate,
          customRef: 'fullCalendarWeek',
          refresh: _ctx.refresh,
          calcHeight: 210,
          displayNowIndicator: true,
          getBusinessHours: _ctx.getBusinessHours,
          selectedAppointmentType: selectedAppointmentType.value,
          doctorId: doctorOpenedId.value,
          doctorName: doctorOpened.value.label,
          changeDate: props.changeDate,
          changeAgendaType: _ctx.changeAgendaType,
          changeAppointmentType: _ctx.changeAppointmentType,
          changePatient: _ctx.changePatient,
          changeDoctor: _ctx.changeDoctor,
          changeDoctorList: _ctx.changeDoctorList,
          eventDurationEditable: false,
          droppable: false,
          displaySettings: displaySettings.value,
          onRefreshed: _cache[4] || (_cache[4] = ($event: any) => (emits('refreshed'))),
          onEventClick: _cache[5] || (_cache[5] = ($event: any) => (emits('openUpdateEvent', {info: $event, item: doctorOpened.value}))),
          onDateClick: _cache[6] || (_cache[6] = ($event: any) => (emits('openAddForm', {info: $event, item: doctorOpened.value}))),
          onOpenSettings: _cache[7] || (_cache[7] = ($event: any) => (emits('openSettings', $event)))
        }, {
          eventContent: _withCtx((arg) => [
            (arg.event.display !== 'background')
              ? _withDirectives((_openBlock(), _createBlock(EsTemplateEvent, {
                  key: 0,
                  appointment: arg,
                  smallDisplay: false,
                  cssClass: 'pleft-04 truncate-patient-name',
                  selectedDoctor: doctorOpened.value,
                  doctors: _ctx.doctors,
                  onRefreshCalendar: _cache[3] || (_cache[3] = ($event: any) => (emits('refreshCalendar')))
                }, null, 8, ["appointment", "selectedDoctor", "doctors"])), [
                  [_directive_tooltip, getTooltip(arg.event)]
                ])
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["getEvents", "calendarView", "selectedDate", "refresh", "getBusinessHours", "selectedAppointmentType", "doctorId", "doctorName", "changeDate", "changeAgendaType", "changeAppointmentType", "changePatient", "changeDoctor", "changeDoctorList", "displaySettings"]),
        _createElementVNode("div", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.doctors, (doctor, index) => {
            return (_openBlock(), _createElementBlock("div", { key: index }, [
              (doctor.selected === true)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: _normalizeClass(["btn-doctor", doctor.opened ? 'btn-doctor-current' : '']),
                    onClick: ($event: any) => (changeDoctorOpened(doctor))
                  }, [
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("img", {
                        src: _unref(SharedService).getIcon('calendar', false, 'shareIcons'),
                        class: _normalizeClass(doctor.opened ? 'item-doctor-current img-white' : 'item-doctor img-primary')
                      }, null, 10, _hoisted_5),
                      _createElementVNode("span", {
                        class: _normalizeClass(["mleft-04", doctor.opened ? 'item-doctor-current' : 'item-doctor'])
                      }, _toDisplayString(doctor.label), 3)
                    ])
                  ], 10, _hoisted_3))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})